import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertCircle, X } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useLocale } from "@/hooks/useLocale";
import { cn } from "@/utils/cn";
import {
  useGetEmployeePayslipDetailQuery,
  useListEmployeePayslipsQuery,
  type IEmployeePayslipListItem,
} from "@/services/(my-space)/employeePayrollApi";
import { useListMyDisputesQuery } from "@/services/(company)/payrollDisputesApi";
import {
  RaiseDisputeDialog,
  type PayslipLineSeed,
} from "../payslip/_components/RaiseDisputeDialog";

const toNumber = (v: string | number | null | undefined): number => {
  if (v === null || v === undefined || v === "") return 0;
  const n = typeof v === "string" ? Number(v.replace(/,/g, "")) : v;
  return Number.isFinite(n) ? n : 0;
};

/** Not a component — the caller passes its own locale-aware formatters in. */
const formatMoney = (
  v: number | string,
  currency: string | null | undefined,
  formatNumber_: (value: number, options?: Intl.NumberFormatOptions) => string,
  formatCurrency_: (value: number, currency: string) => string,
) => {
  const amount = toNumber(v);
  return currency
    ? formatCurrency_(amount, currency)
    : formatNumber_(amount, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
};

interface IProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

/**
 * Two-step "Raise Dispute" entry point for the My Disputes tab: pick the
 * payslip first, then hand its lines to the existing dispute dialog, which is
 * payslip-scoped and needs those lines to seed its rows.
 */
export const RaiseDisputePicker = ({ open, onClose, onSuccess }: IProps) => {
  const { t } = useTranslation("my-space");
  const { formatDate, formatNumber, formatCurrency } = useLocale();
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const { data: payslipsResp, isFetching } = useListEmployeePayslipsQuery(
    { page: 1, limit: 24, sort: "periodStart", sortOrder: "desc" },
    { skip: !open },
  );
  const payslips = useMemo<IEmployeePayslipListItem[]>(
    () => payslipsResp?.data ?? [],
    [payslipsResp],
  );

  // A payslip whose dispute is still open can't be disputed again — same rule
  // the payslip detail page applies to its own Raise Dispute button.
  const { data: disputesResp } = useListMyDisputesQuery(
    { page: 1, limit: 100 },
    { skip: !open },
  );
  const lockedPayslipIds = useMemo(() => {
    const ids = new Set<number>();
    (disputesResp?.data ?? []).forEach((d) => {
      if (d.status === "PENDING" || d.status === "UNDER_REVIEW") {
        ids.add(d.payrollRunItemId);
      }
    });
    return ids;
  }, [disputesResp]);

  // Only fetched once a payslip is picked — the list carries no line items.
  const { data: detailResp, isFetching: isLoadingDetail } =
    useGetEmployeePayslipDetailQuery(selectedId as number, {
      skip: selectedId == null,
    });
  const detail = detailResp?.data;
  const preview = detail?.payslipPreview;
  const currencyCode =
    preview?.currency?.code ?? detail?.netPay.currency.code ?? "";

  const seedLines = useMemo<PayslipLineSeed[]>(() => {
    const earnings = (preview?.earnings ?? []).map((e: any) => ({
      lineId: e.id ?? null,
      lineType: "EARNING" as const,
      name: e.name ?? "",
      currentAmount: toNumber(e.amount),
    }));
    const benefits = (preview?.benefits ?? []).map((e: any) => ({
      lineId: e.id ?? null,
      lineType: "BENEFIT" as const,
      name: e.name ?? "",
      currentAmount: toNumber(e.amount),
    }));
    const deductions = (preview?.deductions ?? []).map((d: any) => ({
      lineId: d.id ?? null,
      lineType: "DEDUCTION" as const,
      name: d.name ?? "",
      currentAmount: toNumber(d.amount),
      lopDetail: d.lopDetail ?? null,
    }));
    return [...earnings, ...benefits, ...deductions];
  }, [preview]);

  const close = () => {
    setSelectedId(null);
    onClose();
  };

  if (!open) return null;

  // Step 2 — payslip chosen and loaded: hand over to the real dispute dialog.
  if (selectedId != null && detail && !isLoadingDetail) {
    return (
      <RaiseDisputeDialog
        open
        payslipId={detail.id}
        currencyCode={currencyCode}
        seedLines={seedLines}
        onClose={close}
        onSuccess={() => {
          setSelectedId(null);
          onSuccess?.();
        }}
      />
    );
  }

  // Step 1 — pick the payslip.
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-sm shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
        <div className="flex items-start justify-between px-5 pt-5 pb-3 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center h-10 w-10 rounded-full bg-warning-muted text-warning">
              <AlertCircle size={20} />
            </div>
            <div>
              <h3 className="text-base font-semibold text-foreground">
                {t("ui.raise_dispute")}
              </h3>
              <p className="text-xs text-muted-foreground mt-0.5">
                {t("ui.choose_the_payslip_you_want_to_dispute")}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={close}
            aria-label={t("ui.cancel")}
            className="text-muted-foreground hover:text-foreground"
          >
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {(isFetching || isLoadingDetail) && (
            <div className="text-sm text-muted-foreground py-10 text-center">
              {isLoadingDetail
                ? t("ui.loading_payslip")
                : t("ui.loading_payslips")}
            </div>
          )}

          {!isFetching && !isLoadingDetail && payslips.length === 0 && (
            <div className="text-sm text-muted-foreground py-10 text-center">
              {t("ui.you_have_no_payslips_to_dispute_yet")}
            </div>
          )}

          {!isFetching && !isLoadingDetail && payslips.length > 0 && (
            <div className="flex flex-col gap-2">
              {payslips.map((p) => {
                const locked = lockedPayslipIds.has(p.id);
                return (
                  <button
                    key={p.id}
                    type="button"
                    disabled={locked}
                    onClick={() => setSelectedId(p.id)}
                    className={cn(
                      "flex items-center justify-between gap-3 w-full text-start border rounded-xs px-4 py-3 transition-colors",
                      locked
                        ? "border-border bg-muted cursor-not-allowed"
                        : "border-border hover:border-warning hover:bg-warning-muted/40",
                    )}
                  >
                    <div className="flex flex-col min-w-0">
                      <span
                        className={cn(
                          "text-sm font-medium",
                          locked ? "text-muted-foreground" : "text-foreground",
                        )}
                      >
                        {p.payPeriod}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {t("ui.paid_on_date", {
                          date:
                            p.payDate && !isNaN(new Date(p.payDate).getTime())
                              ? formatDate(new Date(p.payDate), "dd MMM yyyy")
                              : "—",
                        })}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <span
                        className={cn(
                          "text-sm font-semibold",
                          locked ? "text-muted-foreground" : "text-foreground",
                        )}
                      >
                        {formatMoney(
                          p.netSalary,
                          p.currency?.code,
                          formatNumber,
                          formatCurrency,
                        )}
                      </span>
                      {locked && (
                        <Badge variant="warning">{t("ui.dispute_open")}</Badge>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 px-5 py-4 border-t border-border">
          <Button variant="outline" onClick={close}>
            {t("ui.cancel")}
          </Button>
        </div>
      </div>
    </div>
  );
};
